import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChange,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { WarnDialogComponent } from '../../../warn-dialog/warn-dialog.component';
import { MatButtonModule } from '@angular/material/button';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { UniversalSearchService } from '../../../universal-search/services/universal-search.service';
import { AdminService } from '../../admin.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-upload-content',
  imports: [
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatAutocompleteModule,
  ],
  templateUrl: './upload-content.component.html',
  styleUrl: './upload-content.component.scss',
})
export class UploadContentComponent implements OnInit, OnChanges {
  private dialog = inject(MatDialog);
  private usServ = inject(UniversalSearchService);
  private adminService = inject(AdminService);
  private snacky = inject(MatSnackBar);

  @Input() paramItem = null;
  contentForm: FormGroup;
  contentTypes = [
    { value: 'Disclosures', name: 'Disclosures' },
    { value: 'AiPrompt', name: 'AI Prompt' },
    { value: 'TermsAndDefinitions', name: 'Terms and Definitions' },
  ];
  units = [];
  names = [];
  selectedUnit: { value: string; display: string };
  unsubscribe = new Subject();

  ngOnInit(): void {
    this.contentForm = new FormGroup({
      unit: new FormControl('', Validators.required),
      contentType: new FormControl('', Validators.required),
      name: new FormControl(''),
      content: new FormControl('', Validators.required),
    });

    this.adminService.getContentNames().subscribe(res => (this.names = res));

    this.contentForm.get('contentType').valueChanges.subscribe(value => {
      if (value === 'AiPrompt') {
        this.contentForm.get('name').setValidators(Validators.required);
      } else {
        this.contentForm.get('name').clearValidators();
        this.contentForm.get('name').setValue(null);
      }
      this.contentForm.get('name').updateValueAndValidity();
    });

    this.contentForm.controls['unit'].valueChanges
      .pipe(
        takeUntil(this.unsubscribe),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(value => {
        if (typeof value === 'string') {
          this.adminService.getUnitList(value, true).subscribe(resp => {
            const newUnitsList = resp.names.map(unitRes => ({
              value: unitRes.id,
              display: unitRes.name,
            }));
            this.units = newUnitsList;
          });
        }
      });
  }

  ngOnChanges(simpleChange: { [propName: string]: SimpleChange }) {
    if (simpleChange?.['paramItem']?.currentValue) {
      if (
        this.contentForm.controls['content'].value ||
        this.contentForm.controls['contentType'].value ||
        this.selectedUnit ||
        this.contentForm.controls['name'].value
      ) {
        const dialogRef = this.dialog.open(WarnDialogComponent, {
          panelClass: 'warn-dialog',
          data: {
            headline: 'Warning',
            content:
              'There is already some content in the edit tool. The selected text will overwrite all current data in the content editor. Are you sure you want to overwrite the current content?',
            confirm: 'Yes',
          },
        });
        dialogRef.afterClosed().subscribe(result => {
          if (result === 'continue') {
            this.contentForm.controls['content'].setValue(
              simpleChange?.['paramItem']?.currentValue.content
            );
            this.contentForm.controls['contentType'].setValue(
              simpleChange?.['paramItem']?.currentValue.contentType
            );
            this.contentForm.controls['name'].setValue(
              simpleChange?.['paramItem']?.currentValue.name
            );
            this.contentForm.controls['unit'].setValue(
              simpleChange?.['paramItem']?.currentValue.unit
            );
            this.selectedUnit = simpleChange?.['paramItem']?.currentValue.unit;
          }
        });
      } else {
        this.contentForm.controls['content'].setValue(
          simpleChange?.['paramItem']?.currentValue.content
        );
        this.contentForm.controls['contentType'].setValue(
          simpleChange?.['paramItem']?.currentValue.contentType
        );
        this.contentForm.controls['name'].setValue(
          simpleChange?.['paramItem']?.currentValue.name
        );
        this.contentForm.controls['unit'].setValue(
          simpleChange?.['paramItem']?.currentValue.unit
        );
        this.selectedUnit = simpleChange?.['paramItem']?.currentValue.unit;
      }
    }
  }

  changeUnitSelection(ev) {
    this.selectedUnit = ev.option.value;
  }

  onSubmit() {
    if (this.contentForm.valid && this.selectedUnit) {
      const payloadData = {
        unitId: this.selectedUnit.value,
        type: this.contentForm.value.contentType,
        content: this.contentForm.value.content,
      };
      if (this.contentForm.value.name)
        payloadData['name'] = this.contentForm.value.name;
      this.adminService.postUploadContent(payloadData).subscribe({
        next: () => {
          this.snacky.open(`Success! Item successfully saved.`, 'Close', {
            duration: 10000,
          });
        },
        error: () => {
          this.snacky.open(
            `Error. An unexpected error occurred while attempting to save this item.`,
            'Close',
            {
              duration: 10000,
            }
          );
        },
      });
    }
  }

  onReset() {
    const dialogRef = this.dialog.open(WarnDialogComponent, {
      panelClass: 'warn-dialog',
      data: {
        headline: 'Warning',
        content:
          'This will clear all input fields. Are you sure you want to do this?',
        confirm: 'Yes',
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'continue') {
        this.contentForm.reset();
        this.selectedUnit = null;
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    const reader = new FileReader();

    reader.onload = () => {
      const fileContent = reader.result as string;

      if (file.type === 'application/json' || file.type === 'text/plain') {
        this.contentForm.controls['content'].setValue(fileContent);
      } else if (file.type === 'text/csv') {
        const parsedContent = this.parseCsv(fileContent);
        this.contentForm.controls['content'].setValue(parsedContent);
      }
    };

    reader.readAsText(file);
  }

  parseCsv(content: string): string {
    const lines = content.split('\n').filter(line => line.trim() !== '');
    const headers = lines[0].split(',').map(header => header.trim());

    const json = lines.slice(1).map(line => {
      const values = line.split(',').map(value => value.trim());
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = values[index] || null;
      });
      return obj;
    });

    return json.length === 1 ? JSON.stringify(json[0]) : JSON.stringify(json);
  }

  displayFn(ev) {
    return ev?.display || null;
  }
}
